import { z } from 'zod';
import { INCIDENT_NIVEAUX_CRITICITE, MOUVEMENT_TYPES } from '../constants/statuses.js';
import { paginationQuerySchema } from './common.js';
/**
 * Logistique (Sprint 5) — transport physique des cartes/lots entre sites
 * (dépôt central → agence), stocks par site, incidents. Les transitions de
 * statut de carte pilotées par ce module (`prete → expediee`,
 * `expediee → recue_agence`) restent définies dans
 * `../constants/statuses.ts` (`CARTE_TRANSITIONS`) et appliquées via
 * `apps/api/src/lib/carte-transitions.ts` — ces schémas ne valident que la
 * forme des requêtes.
 */
export const createMouvementLogistiqueSchema = z
    .object({
    typeMouvement: z.enum(MOUVEMENT_TYPES),
    carteSocialeId: z.number().int().positive().optional(),
    lotCarteId: z.number().int().positive().optional(),
    siteSourceId: z.number().int().positive(),
    siteDestinationId: z.number().int().positive(),
    transporteurNom: z.string().max(160).optional(),
    referenceTransport: z.string().max(80).optional(),
})
    .refine((data) => data.carteSocialeId !== undefined || data.lotCarteId !== undefined, {
    message: 'Précisez au moins une carte sociale ou un lot de cartes',
    path: ['carteSocialeId'],
});
export const confirmerReceptionMouvementSchema = z.object({
    commentaire: z.string().max(1000).optional(),
});
/**
 * Nombre maximal de cartes par expédition groupée. Une expédition crée une
 * ligne `MouvementLogistique` PAR carte (le schéma fourni lie un mouvement à
 * une carte, pas à une collection) : la borne évite qu'une seule requête
 * ouvre une transaction de plusieurs milliers d'écritures.
 */
export const MAX_CARTES_PAR_EXPEDITION = 500;
/**
 * Expédition groupée (ajoutée après le Sprint 5). Le formulaire d'origine ne
 * permettait d'expédier qu'une carte à la fois, et exigeait son identifiant
 * technique interne — un nombre que l'interface n'affichait nulle part. Une
 * agence recevant un lot entier de cartes fraîchement imprimées, l'expédition
 * se fait désormais par sélection de cartes `prete`.
 *
 * Chaque carte donne lieu à son propre mouvement (`typeMouvement` limité à
 * `sortie`/`transfert`, les seuls types qui déclenchent `prete → expediee`
 * côté API) afin que la réception puisse rester carte par carte en cas de
 * colis partiellement perdu.
 */
export const createExpeditionGroupeeSchema = z.object({
    carteSocialeIds: z
        .array(z.number().int().positive())
        .min(1, 'Sélectionnez au moins une carte')
        .max(MAX_CARTES_PAR_EXPEDITION),
    typeMouvement: z.enum(['sortie', 'transfert']),
    siteSourceId: z.number().int().positive(),
    siteDestinationId: z.number().int().positive(),
    transporteurNom: z.string().max(160).optional(),
    referenceTransport: z.string().max(80).optional(),
});
/**
 * Réception groupée — pendant symétrique de l'expédition groupée : expédier
 * 50 cartes en une action pour devoir confirmer 50 réceptions une par une
 * laisserait le parcours à moitié utilisable.
 */
export const confirmerReceptionsGroupeeSchema = z.object({
    mouvementIds: z
        .array(z.number().int().positive())
        .min(1, 'Sélectionnez au moins un mouvement')
        .max(MAX_CARTES_PAR_EXPEDITION),
    commentaire: z.string().max(1000).optional(),
});
export const listMouvementsQuerySchema = paginationQuerySchema.extend({
    siteId: z.coerce.number().int().positive().optional(),
    statutMouvement: z.string().max(20).optional(),
    typeMouvement: z.string().max(20).optional(),
});
export const createIncidentLogistiqueSchema = z.object({
    typeIncident: z.string().min(1).max(60),
    referenceEntite: z.string().max(60).optional(),
    description: z.string().min(1),
    niveauCriticite: z.enum(INCIDENT_NIVEAUX_CRITICITE).optional(),
});
export const listIncidentsQuerySchema = paginationQuerySchema.extend({
    statutIncident: z.string().max(20).optional(),
});
//# sourceMappingURL=logistique.js.map